import { useEffect, useMemo, useState } from "react";
import {
  SECTIONS, type SectionName, type FieldMeta,
} from "@klubora/shared";
import { useTenantSettings } from "../../hooks/useTenantSettings";
import { useAuditEmails } from "../../hooks/useAuditEmails";
import { writeTenantSetting } from "../../lib/writeTenantSetting";
import { TextField } from "./TextField";
import { NumberField } from "./NumberField";
import { ColorPickerField } from "./ColorPickerField";
import { AssetUploadField } from "./AssetUploadField";
import { AuditFootnote } from "./AuditFootnote";
import { EditorFooter } from "./EditorFooter";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../../lib/tokens";

interface Props {
  tenantId: string;
  section: Exclude<SectionName, "vocabulary">; // vocabulary uses VocabularyEditor
}

const GROUP_LABELS: Record<string, string> = {
  colors:   "Cores",
  logos:    "Logos",
  email:    "Email",
  links:    "Links",
  contact:  "Contato",
  policy:   "Políticas",
  nouns:    "Substantivos",
  sections: "Rótulos de seção",
};

export function TenantSettingsEditor({ tenantId, section }: Props) {
  const { values, audit, loading } = useTenantSettings(tenantId, section);
  const auditUserIds = useMemo(
    () => Object.values(audit).map((a) => a.updated_by).filter((x): x is string => !!x),
    [audit],
  );
  const emails = useAuditEmails(auditUserIds);

  const [form, setForm] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading) setForm({ ...(values as Record<string, unknown>) });
  }, [loading, values]);

  const dirty = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(form)) {
      if (JSON.stringify(form[k]) !== JSON.stringify((values as Record<string, unknown>)[k])) {
        out[k] = form[k];
      }
    }
    return out;
  }, [form, values]);

  const isDirty = Object.keys(dirty).length > 0;

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await writeTenantSetting(tenantId, section, dirty);
      // realtime sub will refresh `values`; form re-syncs in the useEffect above
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => setForm({ ...(values as Record<string, unknown>) });

  if (loading) {
    return <div style={{ color: colors.stone, padding: space.lg }}>Carregando...</div>;
  }

  const meta = SECTIONS[section].meta as Record<string, FieldMeta>;
  const defaults = SECTIONS[section].defaults as Record<string, unknown>;
  // This generic editor only has primitive widgets (text/number/color/asset).
  // Object/array-valued keys — e.g. app.prospect_popup, a nested JSONB config —
  // have no widget here: the else-branch would render String(val) → the literal
  // "[object Object]" and any edit would 400 against the object schema. They are
  // owned by a dedicated admin surface, so skip them entirely (a group left with
  // no editable field then disappears too). A key flagged `hidden` in its meta
  // is skipped the same way: it has a dedicated surface and must not be written
  // from here (KLUB-264, the "Todos" chip positions).
  const isEditableField = (key: string) => {
    if (meta[key].hidden) return false;
    const d = defaults[key];
    return d === null || typeof d !== "object";
  };
  const groups = new Map<string, string[]>();
  for (const key of Object.keys(meta)) {
    if (!isEditableField(key)) continue;
    const g = meta[key].group;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(key);
  }

  const renderField = (key: string) => {
    const m = meta[key];
    const val = form[key];
    const setVal = (next: unknown) => setForm((f) => ({ ...f, [key]: next }));

    let widget;
    if (m.widget === "color") {
      widget = <ColorPickerField label={m.label} value={(val as string) ?? "#000000"} onChange={setVal} />;
    } else if (m.widget === "asset") {
      widget = <AssetUploadField label={m.label} value={(val as string | null) ?? null}
                                 tenantId={tenantId} variant={key.includes("dark") ? "dark" : "light"}
                                 onChange={setVal} />;
    } else if (m.widget === "number") {
      widget = <NumberField label={m.label} value={(val as number) ?? 0} onChange={setVal} />;
    } else {
      widget = <TextField label={m.label} value={String(val ?? "")} placeholder={m.placeholder} onChange={setVal} />;
    }
    return (
      <div key={key}>
        {widget}
        <AuditFootnote audit={audit[key]} emails={emails} />
      </div>
    );
  };

  return (
    <div>
      {[...groups.entries()].map(([groupKey, keys]) => (
        <section key={groupKey} style={{ marginBottom: space.xxl }}>
          <h3 style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, marginBottom: space.md }}>
            {GROUP_LABELS[groupKey] ?? groupKey}
          </h3>
          <div style={{ background: colors.white, border: `1px solid ${colors.fog}`,
                        borderRadius: radius.md, boxShadow: shadow.sm, padding: space.lg }}>
            {keys.map(renderField)}
          </div>
        </section>
      ))}

      <EditorFooter
        dirty={isDirty}
        saving={saving}
        error={error}
        onCancel={onCancel}
        onSave={onSave}
      />
    </div>
  );
}
